// libs
import React, { memo, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

// api
import { likedOrDislikedReview } from 'shared/api/product-details';

//analytics
import NFA from 'shared/helpers/analytics/NFAnalytics';

// helpers
import {
  setReviewToBeLiked,
  getStoredReviewToBeLiked,
  removeReviewToBeLiked,
} from 'shared/components/ReviewCard/Helpful/helpers';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'helpers/newRelic/constants';

// constants
import { AUTH_URI } from 'shared/constants/uri';
import { STATUS, statusValues, HELPFUL_TEXTS } from './helpers';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';

// styles
import {
  Container,
  Title,
  IconWrapper,
  Description,
  HelpfulText,
} from 'shared/components/ReviewCard/Helpful/styles';

// types
import {
  IHelpfulProps,
  IReviewMarkedHelpfulApiError,
  IReviewMarkedHelpfulApiResponse,
} from 'shared/components/ReviewCard/Helpful/types';

// icon
import ThumbsUp from '@nykaa/ui-components/Icons/thumbs-up';
import ThumbsUpFilled from '@nykaa/ui-components/Icons/thumbs-up-filled';

const REVIEW_HELPFUL_CLICKED = 'review_helpful_clicked';

function Helpful({
  id,
  productId,
  likeCount,
  isLoggedInUser,
  reviewMarkedHelpful,
  setIsLikedByUser,
}: IHelpfulProps) {
  const history = useHistory();
  const location = useLocation();

  const getHelpfulDescription = () => {
    if (reviewMarkedHelpful) {
      return `${HELPFUL_TEXTS.HELPFUL_PREFIX} ${likeCount} ${HELPFUL_TEXTS.HELPFUL_SUFIX}`;
    } else {
      return `${likeCount} ${HELPFUL_TEXTS.HELPFUL_SUFIX}`;
    }
  };

  const redirectToAuth = useCallback(() => {
    const params = new URLSearchParams();

    params.set('redirectURL', (location.pathname + location.search).replace('?', '@'));

    const finalUrl = AUTH_URI.PATH + AUTH_URI.LOGIN_OPTIONS + '&' + params.toString();

    history.push(finalUrl);
  }, [history, location.pathname, location.search]);

  const onGuestUserClick = useCallback(() => {
    setReviewToBeLiked(String(id));
    redirectToAuth();
  }, [id, redirectToAuth]);

  const onLikedOrDislikedReview = useCallback(() => {
    const action = reviewMarkedHelpful ? HELPFUL_TEXTS.REMOVE : HELPFUL_TEXTS.ADD;

    likedOrDislikedReview(productId, String(id), action)
      .then((res: IReviewMarkedHelpfulApiResponse) => {
        if (res.error === false) {
          setIsLikedByUser(id, !reviewMarkedHelpful);
        }
      })
      .catch((err: IReviewMarkedHelpfulApiError) => {
        log({
          errTitle: 'Error in helpful review api',
          error: err,
          tags: { [TAG_IDENTIFIER.MODULE]: HELPFUL_TEXTS.HELPFUL_REVIEW },
        });
      });
  }, [id, productId, reviewMarkedHelpful, setIsLikedByUser]);

  useEffect(() => {
    if (isLoggedInUser === false) return;

    const reviewToBeLiked = getStoredReviewToBeLiked();

    // there is no review which needs to be liked
    if (Object.keys(reviewToBeLiked).length === 0) {
      return;
    }

    // the review to be liked has not mounted yet
    if (reviewToBeLiked.id !== String(id)) {
      return;
    }

    // remove Review to be liked from session storage
    if (
      reviewToBeLiked.apiCalledStatus === statusValues[STATUS.CALLED] ||
      reviewToBeLiked.apiCalledStatus === statusValues[STATUS.FAILED]
    ) {
      removeReviewToBeLiked();
    }
    // api has been called, update setIsLikedByUser
    if (reviewToBeLiked.apiCalledStatus === statusValues[STATUS.CALLED]) {
      setIsLikedByUser(reviewToBeLiked.id, true);
    }
  }, [id, isLoggedInUser, setIsLikedByUser]);

  const onHelpfulClicked = () => {
    if (isLoggedInUser) onLikedOrDislikedReview();
    else onGuestUserClick();
    NFA.track({
      event: REVIEW_HELPFUL_CLICKED,
      type: TRACK_TYPE.EVENT,
    });
  };
  return (
    <Container>
      <Title onClick={onHelpfulClicked}>
        <IconWrapper>
          {reviewMarkedHelpful ? <ThumbsUpFilled size="16px" /> : <ThumbsUp size="16px" />}
        </IconWrapper>
        <HelpfulText>{HELPFUL_TEXTS.HELPFUL}</HelpfulText>
      </Title>
      {likeCount > 0 && (
        <Description className="user-help-count">{getHelpfulDescription()}</Description>
      )}
    </Container>
  );
}

export default memo(Helpful);
